import { Button } from 'primereact/button';
import { useState } from 'react';

import { PermissionDeniedMessages } from '@/components/providers/PermissionsContextProvider/Permissions.types';
import { usePermissionGate } from '@/components/providers/PermissionsContextProvider/usePermissionGate';
import { useAddConditionNoteMutation } from '@/components/shared/api/mutations/conditions';
import {
    MentionTextarea,
    useMentionSuggestions,
} from '@/components/shared/components/MentionTextarea';
import { Feature } from '@/components/shared/constants/features';

import {
    ErrorText,
    FormActions,
    FormLabel,
    FormWrapper,
} from './ApplicantConditions.StyledComponents';
import { ResolveConditionOptions } from './ApplicantConditions.types';
import { NoteDropTarget } from './ConditionCard';

interface Props {
    applicantId: string;
    conditionId: string;
    canResolve?: boolean;
    onResolve?: (options?: ResolveConditionOptions) => void;
    onAttachDocument?: () => void;
}

export const ConditionNoteForm = ({
    applicantId,
    conditionId,
    canResolve,
    onResolve,
    onAttachDocument,
}: Props) => {
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const mentionSuggestions = useMentionSuggestions(applicantId);

    const { mutate: saveNote, isPending: isSavingNote } = useAddConditionNoteMutation();
    const isPending = isSavingNote;

    const resolveGate = usePermissionGate(
        Feature.ProtectedConditions,
        PermissionDeniedMessages.ResolveCondition,
        canResolve,
    );

    const validateNote = () => {
        const trimmed = note.trim();
        if (!trimmed) {
            setError('A note is required.');
            return null;
        }
        setError(null);
        return trimmed;
    };

    const handleSave = () => {
        const trimmed = validateNote();
        if (!trimmed) return;
        saveNote(
            { applicantId, conditionId, note: trimmed, isResolutionNote: false },
            {
                onSuccess: () => setNote(''),
                onError: () => setError('Failed to save note. Please try again.'),
            },
        );
    };

    // Carry the in-progress draft into the Resolve modal so the reviewer doesn't retype it.
    const handleResolveClick = resolveGate.gateAction(() =>
        onResolve?.({ initialNote: note.trim() || undefined }),
    );

    return (
        <FormWrapper>
            <FormLabel htmlFor={`add-note-${conditionId}`}>Add a Note</FormLabel>
            <NoteDropTarget>
                <MentionTextarea
                    spellCheck
                    id={`add-note-${conditionId}`}
                    data-testid={`condition-note-input-${conditionId}`}
                    placeholder="Describe progress, what you verified, or next steps…"
                    value={note}
                    onChange={(val) => {
                        setNote(val);
                        if (error) setError(null);
                    }}
                    hasError={Boolean(error)}
                    suggestions={mentionSuggestions}
                />
            </NoteDropTarget>
            {error ? <ErrorText>{error}</ErrorText> : null}
            <FormActions style={{ width: '33vw', minWidth: 400 }}>
                {onAttachDocument ? (
                    <Button
                        label="Attach Document"
                        icon="pi pi-paperclip"
                        severity="info"
                        outlined
                        onClick={onAttachDocument}
                        disabled={isPending}
                        data-testid={`condition-attach-document-${conditionId}`}
                        style={{ flex: 1 }}
                    />
                ) : null}
                <Button
                    label="Save Note"
                    severity="info"
                    onClick={handleSave}
                    loading={isSavingNote}
                    disabled={isPending}
                    data-testid={`condition-save-note-${conditionId}`}
                    style={{ flex: 1 }}
                />
            </FormActions>
            {onResolve ? (
                <FormActions style={{ width: '33vw', minWidth: 400 }}>
                    <Button
                        label="Mark Task/Condition Resolved"
                        icon="pi pi-check"
                        severity="success"
                        onClick={handleResolveClick}
                        disabled={isPending}
                        aria-disabled={resolveGate.disabled || undefined}
                        title={resolveGate.title}
                        data-testid={`condition-mark-resolved-${conditionId}`}
                        style={{
                            width: '100%',
                            ...(resolveGate.disabled
                                ? { opacity: 0.6, cursor: 'not-allowed' }
                                : null),
                        }}
                    />
                </FormActions>
            ) : null}
        </FormWrapper>
    );
};
